// src/components/EndOfTrialModal.jsx 
import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { FaTimes, FaRocket } from "react-icons/fa";
import AiFeedbackModule from "./AiFeedback";

// --- Helpers ---
function parseNum(val) {
  if (typeof val === "number") return val;
  return Number(String(val || "").replace(/[^\d.-]/g, "")) || 0;
}

function parseRiskToReward(value) {
  if (value == null) return null;
  const str = String(value).trim();
  const colon = str.match(/^\s*(-?\d*\.?\d+)\s*:\s*(-?\d*\.?\d+)\s*$/);
  if (colon) {
    const a = parseFloat(colon[1]);
    const b = parseFloat(colon[2]);
    if (a !== 0 && b !== 0) return b / Math.abs(a);
  }
  return null;
}

export default function EndOfTrialModal({ userId, accountId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. Fetch Data Independently (since Stats page is hidden)
  useEffect(() => {
    async function fetchData() {
      if (!userId || !accountId) return;
      try {
        const ref = collection(db, "users", userId, "accounts", accountId, "journal");
        const snap = await getDocs(ref);
        const data = snap.docs.map(d => d.data());
        
        // Basic filtering (exclude deposits, withdrawals, dnt)
        const trades = data.filter(r => {
           const type = String(r["Funding Type"] || "").toLowerCase();
           const pos = String(r["Position"] || "").toLowerCase();
           return type !== "deposit" && type !== "withdrawal" && pos !== "did not take";
        });
        
        setRows(trades);
      } catch (e) {
        console.error("Failed to load trial stats", e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId, accountId]);

  // 2. Calculate Stats Payload for AI
  const statsPayload = useMemo(() => {
    if (!rows.length) return {};

    const winCount = rows.filter(r => r.Win === true || r.Win === "TRUE").length;
    const lossCount = rows.filter(r => r.Loss === true || r.Loss === "TRUE").length;
    const tradeCount = rows.length;
    const winPct = tradeCount ? Math.round((winCount / tradeCount) * 100) : 0;
    const overallIncrease = rows.reduce((acc, r) => acc + parseNum(r["Actual Reward"]), 0);

    // R:R Calculation
    let rrSum = 0, rrCount = 0;
    rows.forEach(r => {
      const rr = parseRiskToReward(r["Actual Risk to Reward"] || r["Risk/Reward"]);
      const pnl = parseNum(r["Actual Reward"]);
      if (rr != null && pnl !== 0) {
        rrSum += pnl < 0 ? -Math.abs(rr) : Math.abs(rr);
        rrCount++;
      }
    });
    const averageRiskToReward = rrCount ? rrSum / rrCount : 0;

    // Mood Stats
    const posMoods = rows.filter(r => ["1", "2"].includes(String(r["Mood/Mindset"])));
    const negMoods = rows.filter(r => ["4", "5"].includes(String(r["Mood/Mindset"])));
    
    const calcWinRate = (arr) => {
        if(!arr.length) return 0;
        const w = arr.filter(r => r.Win === true || r.Win === "TRUE").length;
        return Math.round((w / arr.length) * 100);
    };

    return {
      tradeCount,
      winPct,
      lossCount,
      overallIncrease,
      averageRiskToReward,
      moodTradeCount: rows.filter(r => r["Mood/Mindset"]).length,
      winRatePositive: calcWinRate(posMoods),
      winRateNegative: calcWinRate(negMoods),
    };
  }, [rows]);

  const handleClose = () => {
    // Save flag to prevent showing again
    localStorage.setItem(`trapound_trial_seen_${userId}`, "true");
    onClose();
  };

  if (loading) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="bg-white dark:bg-gray-900 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl border-2 border-blue-500 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-6 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-full">
              <FaRocket className="text-yellow-400 text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-white">Your Trial Ends Today</h2>
              <p className="text-blue-100 text-sm">Here is your performance analysis</p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="text-white/70 hover:text-white transition"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 flex-1 space-y-6">
          {/* Intro Text */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
            <p className="text-gray-800 dark:text-gray-200 text-center font-medium">
              You've logged <strong>{statsPayload.tradeCount} trades</strong> during your trial. 
              Your account will automatically renew to the standard plan tomorrow unless canceled.
            </p>
          </div>

          {/* AI Module - Directly rendered without extra wrapper */}
          <AiFeedbackModule 
            userId={userId} 
            accountId={accountId}
            stats={statsPayload}
            filteredRows={rows}
            activeFilters={{ dateFilter: "all" }} // analyzing all trial data
            customTitle="🎓 End of Trial Analysis"
            hideLocalInsights={true} // Focus purely on the AI narrative
            autoGenerate={true}      // Trigger analysis automatically
            hideControls={true}      // Hide header and buttons
          />

          {/* Close Button */}
          <div className="flex flex-col items-center gap-4 pt-4 border-t dark:border-gray-700">
            <button 
              onClick={handleClose}
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold py-3 px-12 rounded-full shadow transition"
            >
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}